import { type OpeningHours, isOpeningHours } from "./openingHoursLib"
import { type Eligibilities, eligibilitySchema } from "./eligibilities"

import { type OrientationData } from "@heyhabito/orientation-client"
import {
  type AsyncRecStatus,
  completionCelebratedSchema,
  type CompletionCelebrated,
  documentsUploadStatus,
  type DocumentsUploadStatus,
  type ExAppId,
  type FactFindAvailability,
  factFindAvailabilitySchema,
  type FactFindCompletionStatuses,
  type ManualAdviceMethod,
  type ChatPreference,
  manualAdviceMethodSchema,
  type MipJourney,
  mipJourneySchema,
  type MortgageDetails,
  mortgageDetailsSchema,
  type MortgageTrackerJourneySummary,
  mortgageTrackerJourneySummarySchema,
  type MyAccount,
  myAccountSchema,
  partialFactFindCompletionOverview,
  postSubmissionTimelineSchema,
  type PostSubmissionTimeline,
  type Token,
  type Url,
} from "./types"

import fromPairs from "lodash/fromPairs"
import toPairs from "lodash/toPairs"
import { v4 as uuidv4 } from "uuid"

import { type Either } from "fp-ts/lib/Either"
import * as t from "io-ts"
import { PathReporter } from "io-ts/lib/PathReporter"

export const validate = async <A, O>(
  schema: t.Type<A, O, unknown>,
  json: unknown
): Promise<A> => {
  const result = schema.decode(json)
  if (result._tag !== "Right") {
    console.error(`Failed to parse, ${PathReporter.report(result)}`)
    throw new Error(`Failed to parse, ${PathReporter.report(result)}`)
  }
  return result.right
}

export type RequestId = string

export const newRequestId: () => string = uuidv4

export const mkHeaders = (token: Token, requestId?: RequestId): HeadersInit => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }
  if (requestId) {
    headers["x-request-id"] = requestId
  }
  return headers
}

const getRequest = (
  url: Url,
  token: Token,
  requestId?: RequestId
): Promise<Response> =>
  fetch(url, {
    headers: mkHeaders(token, requestId),
  })

const postRequest = (
  url: Url,
  token: Token,
  requestId?: RequestId,
  body?: Partial<Record<string, unknown>>
): Promise<Response> =>
  fetch(url, {
    headers: mkHeaders(token, requestId),
    method: "POST",
    body: JSON.stringify(body),
  })

export class NotOkError extends Error {
  readonly url: string
  readonly status: number
  readonly statusText: string
  readonly body?: string
  constructor(message: string, response: Response, body?: string) {
    super(message)
    this.name = "NotOkError"
    // See https://github.com/microsoft/TypeScript/blob/master/lib/lib.dom.d.ts#L12816
    // for fields of `Response`
    this.url = response.url
    this.status = response.status
    this.statusText = response.statusText
    this.body = body
  }
}

export class AuthError extends NotOkError {
  constructor(message: string, response: Response, body?: string) {
    super(message, response, body)
    this.name = "AuthError"
  }
}

const applicationsUrl = (exAppId: ExAppId): string =>
  `/api/mortgages/residential/applications/${exAppId}`

export const getMyAccount = async (
  token: Token,
  requestId?: RequestId
): Promise<Either<t.Errors, MyAccount>> => {
  const res = await getRequest(`/api/my-account`, token, requestId)
  if (res.status !== 200) {
    const body = await res.text()

    if (res.status === 401 || res.status === 403) {
      throw new AuthError("error calling getMyAccount", res, body)
    } else {
      throw new NotOkError("error calling getMyAccount", res, body)
    }
  }
  const json = await res.json()
  return myAccountSchema.decode(json)
}

export const getCompletionCelebration = async (
  token: Token,
  exAppId: ExAppId
): Promise<CompletionCelebrated> => {
  const res = await fetch(`/api/dashboard/completion-celebration/${exAppId}`, {
    headers: mkHeaders(token),
  })
  if (res.status !== 200) {
    const body = await res.text()
    throw new NotOkError("error calling getCompletionCelebration", res, body)
  }
  const json = await res.json()
  return validate(completionCelebratedSchema, json)
}

export const postCompletionCelebration = async (
  token: Token,
  exAppId: ExAppId
): Promise<null> => {
  const res = await fetch(`/api/dashboard/completion-celebration/${exAppId}`, {
    headers: mkHeaders(token),
    method: "POST",
  })

  if (res.status !== 204) {
    const body = await res.text()
    throw new NotOkError("error calling postCompletionCelebration", res, body)
  }
  return Promise.resolve(null)
}

export const getFactFindAvailabilities =
  (token: Token, requestId?: RequestId) =>
  async (
    exAppIds: ExAppId[]
  ): Promise<Record<ExAppId, FactFindAvailability>> => {
    const query = exAppIds.map(id => `app=${id}`).join("&")
    const res = await fetch(`/api/dashboard/fact-find-availability?${query}`, {
      headers: mkHeaders(token, requestId),
    })
    if (res.status !== 200) {
      const body = await res.text()
      throw new NotOkError("error calling getFactFindAvailabilities", res, body)
    }
    const json = await res.json()
    return Promise.all(
      toPairs(json).map(([key, value]) =>
        validate(factFindAvailabilitySchema, value).then(validValue => [
          key,
          validValue,
        ])
      )
    ).then(entries => fromPairs(entries))
  }

export const getOpeningHours = async (
  token: Token,
  requestId?: RequestId
): Promise<OpeningHours> => {
  const url = `/api/opening-hours/this-week`
  const res = await getRequest(url, token, requestId)
  const bodyText = await res.clone().text()
  const json = await res.json()
  if (res.status !== 200) {
    const message =
      res.status === 502
        ? `Backend failed to call Contentful (502 from ${url})`
        : `Bad response code (${res.status}) when calling ${url}`
    throw new NotOkError(message, res, bodyText)
  }

  if (isOpeningHours(json)) {
    return json
  } else {
    throw new NotOkError(`Failed to parse JSON from ${url}`, res, bodyText)
  }
}

export const getEligibilities =
  (token: Token, requestId?: RequestId) =>
  async (exAppId: ExAppId): Promise<Eligibilities> => {
    const res = await getRequest(
      `/api/dashboard/eligibilities/${exAppId}`,
      token,
      requestId
    )
    if (res.status !== 200) {
      const body = await res.text()
      throw new NotOkError("error calling getEligibilities", res, body)
    }
    const json = await res.json()
    const eligResp = await validate(eligibilitySchema, json)

    return {
      eligibleForPlus: eligResp.eligibleForPlus || false,
      eligibleForSelfServePlus: eligResp.eligibleForSelfServePlus || false,
      hasFoundSolicitor: eligResp.hasFoundSolicitor,
    }
  }

export const getMortgageDetails =
  (token: Token, requestId?: RequestId) =>
  async (exAppId: ExAppId): Promise<MortgageDetails> => {
    const res = await getRequest(
      `/api/dashboard/applied-mortgage-details/${exAppId}`,
      token,
      requestId
    )
    if (res.status !== 200) {
      const body = await res.text()
      throw new NotOkError("error calling getMortgageDetails", res, body)
    }
    const json = await res.json()

    return validate(mortgageDetailsSchema, json)
  }

export const getMipJourneyStatus = async (
  token: Token,
  requestId?: RequestId
): Promise<MipJourney | null> => {
  const res = await getRequest(
    `/api/mip/dashboard/active-journey-status`,
    token,
    requestId
  )
  if (res.status !== 200) {
    const body = await res.text()
    throw new NotOkError("error calling getMipJourneyStatus", res, body)
  }
  const json = await res.json()

  if (json === null) {
    return null
  } else {
    return validate(mipJourneySchema, json)
  }
}

export const getManualAdviceMethod =
  (token: Token, requestId?: RequestId) =>
  async (exAppId: ExAppId): Promise<ManualAdviceMethod | "Unknown"> => {
    const res = await getRequest(
      `${applicationsUrl(exAppId)}/advice/manual-advice-method`,
      token,
      requestId
    )

    if (res.status !== 200) {
      const body = await res.text()
      throw new NotOkError("error calling getManualAdviceMethod", res, body)
    }
    const json = await res.json()

    if (json === null) {
      return "Unknown"
    } else {
      return validate(manualAdviceMethodSchema, json)
    }
  }

export const getHasAdverseEvents =
  (token: Token, requestId?: RequestId) =>
  async (exAppId: ExAppId): Promise<boolean> => {
    const res = await getRequest(
      `${applicationsUrl(exAppId)}/advice/has-adverse-events`,
      token,
      requestId
    )

    if (res.status !== 200) {
      const body = await res.text()
      throw new NotOkError("error calling getHasAdverseEvents", res, body)
    }
    const json = await res.json()

    return validate(t.boolean, json)
  }

export const postStartManualAdvice =
  (token: Token, requestId?: RequestId) =>
  async (exAppId: ExAppId, preference: ChatPreference): Promise<void> => {
    const res = await postRequest(
      `${applicationsUrl(exAppId)}/advice/start-manual-advice`,
      token,
      requestId,
      preference
    )

    if (res.status !== 200) {
      const body = await res.text()
      throw new NotOkError(
        "error calling /advice/start-manual-advice",
        res,
        body
      )
    }
    return await res.json()
  }

export const getMipPdf = async (
  token: Token,
  journeyId: string,
  mipId: string
): Promise<Blob> => {
  const res = await getRequest(
    `/api/mip/download-pdf/${journeyId}/${mipId}`,
    token
  )
  if (res.status !== 200) {
    const body = await res.text()
    throw new Error("error calling download-pdf: " + res.status + "; " + body)
  }
  return await res.blob()
}

export const getMortgageTrackerJourneySummary = async (
  token: Token,
  requestId?: RequestId
): Promise<MortgageTrackerJourneySummary | null> => {
  const res = await getRequest(
    `/api/mortgage-tracker/active-journey`,
    token,
    requestId
  )
  if (res.status !== 200) {
    const body = await res.text()
    throw new NotOkError("error calling getMortgageTrackerJourneyId", res, body)
  }
  const json = await res.json()
  if (json === null) {
    return null
  } else {
    return validate(mortgageTrackerJourneySummarySchema, json)
  }
}

export const getLenderName = async (
  token: string,
  exAppId: ExAppId
): Promise<string | null> => {
  const mortgageDetails = await getMortgageDetails(token)(exAppId)

  return mortgageDetails.lender && mortgageDetails.lender.name
}

export const getDocumentsUploadStatus = async (
  token: string,
  exAppId: ExAppId
): Promise<DocumentsUploadStatus | null> => {
  const res = await getRequest(
    applicationsUrl(exAppId) + "/documents-ready",
    token,
    exAppId
  )

  if (res.status !== 200) {
    const body = await res.text()

    throw new NotOkError("error calling getDocumentsUploadStatus", res, body)
  }

  const json = await res.json()

  if (json === null) {
    return null
  }

  return validate(documentsUploadStatus, json)
}

export const getFactFindCompletionStatuses = async (
  token: string,
  exAppId: ExAppId
): Promise<FactFindCompletionStatuses | null> => {
  const res = await getRequest(
    applicationsUrl(exAppId) + "/overview-completion",
    token,
    exAppId
  )

  if (res.status !== 200) {
    const body = await res.text()

    throw new NotOkError(
      "error calling getFactFindCompletionOverview",
      res,
      body
    )
  }

  const json = await res.json()

  if (json === null) {
    return null
  }

  const { groups } = await validate(partialFactFindCompletionOverview, json)

  return groups[0].contents
}

export const getOrientationData = async (
  token: string
): Promise<OrientationData | null> => {
  const res = await getRequest(`/api/orientation/data`, token)

  if (res.status === 404) {
    return null
  } else if (res.status !== 200) {
    const body = await res.text()

    throw new NotOkError("error calling getOrientationData", res, body)
  }

  return await res.json()
}

export const getPostSubmissionTimeline = async (
  token: string,
  exAppId: ExAppId
): Promise<PostSubmissionTimeline | null> => {
  const res = await getRequest(
    `/api/post-submission-timeline/post-submission-timeline/${exAppId}`,
    token,
    exAppId
  )

  if (res.status !== 200) {
    const body = await res.text()

    throw new NotOkError("error calling getPostSubmissionTimeline", res, body)
  }

  const json = await res.json()

  if (json === null) {
    return null
  }
  return validate(postSubmissionTimelineSchema, json)
}

export const getAsyncRecStatus = async (
  token: string
): Promise<AsyncRecStatus> => {
  const url = `/api/async-rec-control/async-rec-status/full`
  const res = await getRequest(url, token)

  if (res.status === 200) {
    return await res.json()
  }

  throw new Error(`Failed to get async-rec-status`)
}
